import React, { useState, useEffect } from 'react';
import { auth } from '../../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import Header from './Header';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Users = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);
  const [user, setUser] = useState(null);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [timeframe, setTimeframe] = useState('monthly');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchUsersRanking = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/users/ranking?timeframe=${timeframe}`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUsersRanking();
      setCurrentPage(1); // Reset page on new fetch
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, timeframe]);

  // Filtering users by search term
  const filteredUsers = users.filter(u => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (u.email && u.email.toLowerCase().includes(term)) ||
      (u.uid && u.uid.toLowerCase().includes(term)) ||
      (u.firstName && u.firstName.toLowerCase().includes(term)) ||
      (u.lastName && u.lastName.toLowerCase().includes(term))
    );
  });

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
  const currentUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Helper to color ranks
  const getRankColor = (rank) => {
    if (rank <= 5) return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-700'; // Gold/Top 5
    if (rank <= 10) return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600'; // Silver/Next 5
    if (rank <= 15) return 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-700'; // Bronze/Next 5
    return 'bg-white text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'; // Default
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <Header onToggleMenuClick={toggleMenu} isOpen={isOpen} />
      <div className={`pt-16 px-4 sm:px-6 lg:px-8 transition-all duration-300 ${isOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <main className="max-w-6xl mx-auto py-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              User Ranking & Insights
            </h2>
            <button onClick={fetchUsersRanking} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
                Refresh Data
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Timeframe (Transaction Volume)</label>
                    <div className="flex space-x-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-md w-max overflow-x-auto max-w-full">
                      {['daily', 'weekly', 'monthly', 'yearly', 'all'].map(tf => (
                        <button
                          key={tf}
                          onClick={() => setTimeframe(tf)}
                          className={`px-3 py-1 rounded-md text-sm font-medium capitalize transition-colors ${timeframe === tf ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
                        >
                          {tf}
                        </button>
                      ))}
                    </div>
                </div>
                
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Search Users</label>
                    <input 
                        type="text" 
                        placeholder="Search by Email, Name, or UID..." 
                        value={searchTerm} 
                        onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
                        className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                </div>
            </div>

            {loading ? (
                <p className="text-center py-8">Calculating user rankings...</p>
            ) : (
                <>
                <div className="overflow-x-auto rounded-t-lg">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Volume (₦)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Purchases</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {currentUsers.length > 0 ? currentUsers.map(u => (
                        <tr key={u.uid} className={`border-l-4 hover:opacity-90 ${getRankColor(u.rank)}`}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-bold">#{u.rank}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">{u.firstName} {u.lastName}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">{u.email}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs font-mono">{u.uid.substring(0,8)}...</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm capitalize">{u.role}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold">₦{parseFloat(u.total_volume).toLocaleString()}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right">{u.transaction_count}</td>
                        </tr>
                        )) : (
                            <tr>
                                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                                    No users found matching the criteria.
                                </td>
                            </tr>
                        )}
                    </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {filteredUsers.length > 0 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center px-6 py-4 border border-t-0 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4 sm:mb-0">
                      <span>Show</span>
                      <select 
                        value={itemsPerPage} 
                        onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        className="mx-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 px-2 py-1 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span>entries</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handlePageChange(currentPage - 1)} 
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded text-sm font-medium ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700'}`}
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button 
                        onClick={() => handlePageChange(currentPage + 1)} 
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded text-sm font-medium ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700'}`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
                </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Users;
